import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { actions } from 'store';
import { ModalButtonWithForm } from 'components/common';
import { AdminsAddOrEditForm } from '.';

import type { AdminUser, UserPermission } from 'shared-types';

type FormValues = {
  permissions: UserPermission;
  role: 'admin' | 'superadmin';
};

interface Props {
  disabled?: boolean;
  numberOfAdmins: number;
  numberOfSuperadmins: number;
  targetUser: AdminUser;
  updateAdmins: () => Promise<void>;
}

const AdminEdit = ({
  numberOfAdmins,
  numberOfSuperadmins,
  disabled = false,
  targetUser,
  updateAdmins
}: Props) => {
  const { dispatch } = useDispatch();

  const {
    messages,
    serverConfig: { usersAdminLimit, usersSuperadminLimit }
  } = useReduxState();

  const handleSubmit = async (values: FormValues) => {
    try {
      const permissions =
        values.permissions && values.role === 'admin'
          ? Object.keys(values.permissions).filter(
              (perm) => values.permissions[perm]
            )
          : [];

      const baseRoles = targetUser.roles.filter(
        (role) => role !== 'admin' && role !== 'superadmin'
      );

      const roles = {
        admin: [...baseRoles, 'admin'],
        superadmin: [...baseRoles, 'admin', 'superadmin']
      };

      const response = await api.put(`/admin/users/${targetUser.id}`, {
        permissions,
        roles: roles[values.role]
      });
      if (!response.data.success) throw new Error('error');

      await updateAdmins();

      dispatch(
        actions.addNotice({
          text: messages.settingsEditAdminSuccess,
          type: 'success'
        })
      );
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  const disableSuperadminOption =
    numberOfSuperadmins >= usersSuperadminLimit &&
    !targetUser?.roles?.includes('superadmin');

  const disableAdminOption =
    numberOfAdmins >= usersAdminLimit && !targetUser?.roles?.includes('admin');

  const initialValues = {
    permissions: targetUser.permissions.reduce((obj, permission) => {
      obj[permission] = true;
      return obj;
    }, {}),

    role: targetUser.roles?.includes('superadmin') ? 'superadmin' : 'admin'
  };

  return (
    <ModalButtonWithForm
      buttonCategory="success"
      buttonDisabled={disabled}
      buttonIcon="pen"
      buttonIsDropdown={true}
      buttonText={messages.commonEdit}
      initialValues={initialValues}
      modalActionText={messages.commonSave}
      modalTitle={messages.settingsEditAdmin}
      modalWidth="medium"
      onSubmit={handleSubmit}
      pristineIsValid={false}
    >
      {() => (
        <AdminsAddOrEditForm
          disableAdmins={disableAdminOption}
          disableSuperadmins={disableSuperadminOption}
          targetUser={targetUser}
        />
      )}
    </ModalButtonWithForm>
  );
};

export default AdminEdit;
